import {
  type AgPublicEventType,
  type ColDef,
  type ColumnVisibleEvent,
  type GridApi,
  type NewColumnsLoadedEvent,
} from "ag-grid-community"
import { type AgGridReact } from "ag-grid-react"
import { captureException } from "@sentry/react"
import { dequal } from "dequal"
import React, { useCallback, useEffect, useMemo, useRef } from "react"
import { toast } from "react-toastify"
import { useEventCallback } from "usehooks-ts"

import { type ProductTableType } from "~/constants/productTable"
import { getGridApiById, stateColumnVisibility, useGridDerivedValue } from "~/hooks/useGridState"
import { useRegisteredGridEffect } from "~/hooks/useRegisteredGridEffect"
import { useStableValue } from "~/hooks/useStableValue"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import {
  ProductTableColumn,
  resolveDisplayedColumnsOnDefaultChange,
} from "~/utils/productTableUtils"

import { placeLoadedColumns } from "./utils/columnPlacement"
import {
  chooserColumnIdsOf,
  colIdsOf,
  commitDisplayedColumns,
  invalidViewColumnIds,
  isCompanionColumn,
  liveColumns,
  reconcileAggregation,
  shownManagedColumnIds,
} from "./utils/columnVisibility"

const COLUMN_VISIBILITY_EVENTS: readonly AgPublicEventType[] = ["columnVisible", "newColumnsLoaded"]

/**
 * Reads whether the product table `tableId` shows a column: the grid's answer once it has the
 * column, else the table's grid state's (see {@link stateColumnVisibility}), else whether its base
 * id is a default column. Built per read: the grid and its state move underneath it.
 */
export function columnShownReader(tableId: string, defaultColumnIds: ReadonlySet<string>) {
  const api = getGridApiById(tableId)
  const grid = api && !api.isDestroyed() ? api : undefined
  const fromState = stateColumnVisibility(tableId)
  return (colId: string, baseColId = colId): boolean => {
    const column = grid?.getColumn(colId)
    if (column) return column.isVisible()
    return fromState(colId) ?? defaultColumnIds.has(baseColId)
  }
}

/**
 * The ids among `colIds` the product table `tableId` shows (see {@link columnShownReader}), for
 * reads that only feed some columns. Re-renders only when that set changes.
 */
export function useShownColumnIds(
  tableId: string,
  colIds: readonly string[],
  defaultColumnIds: readonly string[],
): ReadonlySet<string> {
  const stableColIds = useStableValue(colIds, dequal)
  const stableDefaults = useStableValue(defaultColumnIds, dequal)
  const defaultSet = useMemo(() => new Set(stableDefaults), [stableDefaults])
  const select = useCallback(() => {
    const isShown = columnShownReader(tableId, defaultSet)
    return new Set(stableColIds.filter((colId) => isShown(colId)))
  }, [tableId, stableColIds, defaultSet])
  return useGridDerivedValue(tableId, select, COLUMN_VISIBILITY_EVENTS, dequal)
}

const STATIC_COLUMN_IDS = Object.values(ProductTableColumn).filter(
  (col) => col !== ProductTableColumn.ProductName && col !== ProductTableColumn.HideColumns,
)

/** The static columns plus `dynamicIds`, less the gated ones, within `overwriteColumnsToShow`. */
function availableColumnIds(
  dynamicIds: readonly string[],
  gatedColumnIdSet: ReadonlySet<string>,
  overwriteColumnsToShow: readonly string[] | undefined,
): string[] {
  const available = [...STATIC_COLUMN_IDS, ...dynamicIds].filter((id) => !gatedColumnIdSet.has(id))
  return overwriteColumnsToShow && overwriteColumnsToShow.length > 0
    ? available.filter((id) => overwriteColumnsToShow.includes(id))
    : available
}

/** The column ids a saved view may hold, and the ones the column chooser lists. */
export function useProductTableColumnIds({
  dynamicColumns,
  extraColumns,
  collectionColumns,
  listCollectionColumns,
  gatedColumnIdSet,
  overwriteColumnsToShow,
}: {
  dynamicColumns: ColDef<IVariantSummary>[]
  extraColumns: ColDef<IVariantSummary>[] | undefined
  collectionColumns: ColDef<IVariantSummary>[]
  listCollectionColumns: boolean
  gatedColumnIdSet: ReadonlySet<string>
  overwriteColumnsToShow: ProductTableColumn[] | undefined
}) {
  const columnDefs = useMemo(
    () => [
      ...dynamicColumns,
      ...(extraColumns ?? []),
      ...(listCollectionColumns ? collectionColumns : []),
    ],
    [dynamicColumns, extraColumns, listCollectionColumns, collectionColumns],
  )

  // Every column a saved view may hold, calendar and always-visible ones included.
  const allColumnIds = useMemo(
    () =>
      availableColumnIds(
        colIdsOf(columnDefs).filter((id) => !isCompanionColumn(id)),
        gatedColumnIdSet,
        overwriteColumnsToShow,
      ),
    [columnDefs, gatedColumnIdSet, overwriteColumnsToShow],
  )

  // A locked column keeps its static id out of the chooser too.
  const columnIdsToSelectFrom = useMemo(() => {
    const lockedIdSet = new Set(colIdsOf(columnDefs.filter((c) => c.lockVisible)))
    return availableColumnIds(
      chooserColumnIdsOf(columnDefs),
      gatedColumnIdSet,
      overwriteColumnsToShow,
    ).filter((id) => !lockedIdSet.has(id))
  }, [columnDefs, gatedColumnIdSet, overwriteColumnsToShow])

  return { allColumnIds, columnIdsToSelectFrom }
}

/**
 * The grid's `onNewColumnsLoaded`: places the columns it gained, each after its canonical
 * neighbour, a replaced calendar block where the last save had it, and a dynamic (extradata) column
 * that missed the applied state at its slot, visibility, width, pinning, sort and aggregation from
 * that state. Everything else stays put. Aggregation follows: colDefs carry no live aggFunc, so a
 * column that became required (a row-group column) while hidden only starts aggregating here.
 */
export function useNewColumnPlacement(
  tableId: string,
  dynamicColumns: ColDef<IVariantSummary>[],
  disableColumnSizingPersistence: boolean,
) {
  const dynamicColIdSet = useMemo(() => new Set(colIdsOf(dynamicColumns)), [dynamicColumns])
  return useEventCallback((event: NewColumnsLoadedEvent<IVariantSummary>) => {
    const { api } = event
    if (api.isDestroyed()) return
    const tookLate = placeLoadedColumns(api, tableId, {
      isLate: (colId) => dynamicColIdSet.has(colId),
      restoreWidths: !disableColumnSizingPersistence,
    })
    if (tookLate && disableColumnSizingPersistence) api.sizeColumnsToFit()
    reconcileAggregation(api)
  })
}

/**
 * Brings aggregation in line with visibility set without it (a drag, a full state apply, an
 * outside `applyColumnState`). A drag hides and re-shows columns as it passes, so it follows once
 * the drag ends. Deferred: `columnVisible` fires mid-apply, before a commit's own aggregation has
 * landed.
 */
export function useAggregationFollowsVisibility(tableId: string) {
  useRegisteredGridEffect(
    tableId,
    (api) => {
      const reconcile = () => queueMicrotask(() => reconcileAggregation(api))
      const onColumnVisible = (event: ColumnVisibleEvent) => {
        if (event.source !== "uiColumnDragged") reconcile()
      }
      api.addEventListener("columnVisible", onColumnVisible)
      api.addEventListener("dragStopped", reconcile)
      return () => {
        if (api.isDestroyed()) return
        api.removeEventListener("columnVisible", onColumnVisible)
        api.removeEventListener("dragStopped", reconcile)
      }
    },
    [],
  )
}

/**
 * Shows a changed default's columns once the grid is registered, compared by value. The grid
 * starts from the defaults its first colDefs were built with.
 */
export function useFollowDefaultColumns(
  tableId: string,
  defaultColumnIds: string[],
  excludedFromAutoAdd: string[],
) {
  const followedDefaultsRef = useRef(defaultColumnIds)
  const follow = useEventCallback((api: GridApi) => {
    const prevDefault = followedDefaultsRef.current
    followedDefaultsRef.current = defaultColumnIds
    if (defaultColumnIds.length === 0 || dequal(prevDefault, defaultColumnIds)) return

    const shown = shownManagedColumnIds(liveColumns(api))
    const next =
      shown.length === 0
        ? defaultColumnIds
        : resolveDisplayedColumnsOnDefaultChange(
            prevDefault,
            defaultColumnIds,
            shown,
            excludedFromAutoAdd,
          )
    if (next) commitDisplayedColumns(api, next)
  })
  useRegisteredGridEffect(tableId, follow, [defaultColumnIds, excludedFromAutoAdd])
}

function showInvalidViewToast() {
  toast.error(
    <div>
      <p className="font-semibold mb-1 text-md">This view is invalid</p>
      <p className="text-md text-fg-subtle">
        There are one or more columns in this view that are no longer available. Please add any
        missing columns and save the view.
      </p>
    </div>,
    { toastId: "invalid-view-toast" },
  )
}

/**
 * The table's `onViewApplied`: flags a view's columns the table no longer has, with a toast and a
 * Sentry warning. Until `ready` (extradata columns load asynchronously, so `allColumnIds` lacks them
 * before), a view applied is checked once it is.
 */
export function useViewColumnCheck({
  tableId,
  type,
  allColumnIds,
  gatedColumnIdSet,
  listCollectionColumnsInChooser,
  ready,
}: {
  tableId: string
  type: ProductTableType
  allColumnIds: readonly string[]
  gatedColumnIdSet: ReadonlySet<string>
  listCollectionColumnsInChooser: boolean
  ready: boolean
}) {
  const report = useEventCallback((viewColumnIds: readonly string[]) => {
    const invalidColumnIds = invalidViewColumnIds(viewColumnIds, {
      allColumnIds,
      gatedColumnIdSet,
      listCollectionColumnsInChooser,
    })
    if (invalidColumnIds.length === 0) return
    captureException(new Error("Invalid view: columns no longer available"), {
      level: "warning",
      tags: { tableId, tableType: type },
      extra: {
        invalidColumnIds,
        displayedColumnIds: viewColumnIds,
        availableColumnIds: allColumnIds,
      },
    })
    showInvalidViewToast()
  })

  const uncheckedRef = useRef<readonly string[] | null>(null)
  useEffect(() => {
    const unchecked = uncheckedRef.current
    if (!ready || !unchecked) return
    uncheckedRef.current = null
    report(unchecked)
  }, [ready, report])

  return useEventCallback((viewColumnIds: readonly string[]) => {
    if (ready) report(viewColumnIds)
    else uncheckedRef.current = viewColumnIds
  })
}

/** Hides one managed column through the one visibility writer. */
export function useHideColumn(gridRef: React.RefObject<AgGridReact<IVariantSummary> | null>) {
  return useCallback(
    (colId: string | undefined) => {
      const api = gridRef.current?.api
      if (!api || colId == null) return
      const shown = shownManagedColumnIds(liveColumns(api))
      commitDisplayedColumns(
        api,
        shown.filter((id) => id !== colId),
      )
    },
    [gridRef],
  )
}
